import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LandingPage from './pages/LandingPage';
import LandingPageV2 from './pages/LandingPageV2';
import PricingPage from './pages/PricingPage';
import ExecutiveDashboard from './components/ExecutiveDashboard';
import InvestigationDashboard from './components/InvestigationDashboard';
import FormBuilder from './components/FormBuilder';
import AuditLogsPage from './components/AuditLogsPage';
import MainLayout from './components/MainLayout';
import PublicComplaintForm from './components/PublicComplaintForm';
import PublicComplaintFormV2 from './components/PublicComplaintFormV2';
import ComplaintTracker from './components/ComplaintTracker';
// Alternador v3/v2 — temporário, enquanto a v3 é avaliada com dado real.
// Quando aprovada, voltar a importar ./components/DisruptiveAnalytics direto.
import AnalyticsSwitch from './components/AnalyticsSwitch';
import SuperSimpleDashboard from './components/SuperSimpleDashboard';
import UnifiedDashboard from './components/UnifiedDashboard';
import './styles/modern-ui.css';
import './styles/mobile-fixes.css';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard.js';
import PremiumAdminDashboard from './components/PremiumAdminDashboard';
import ModernDashboard from './components/ModernDashboard';
import UserRegister from './components/UserRegister';
import SimpleFormManagement from './components/FormManagement';
import UserComplaintDetail from './components/UserComplaintDetail';
import SimpleUserManagement from './components/UserManagement';
import SecurityPage from './components/SecurityPage';
import BrandMark from './components/BrandMark';
import ComplaintApproval from './components/ComplaintApproval';
import PoliciesManager from './components/PoliciesManager';

// Flag para testar nova versão do formulário
const USE_NEW_FORM = true;

// Configuração do Axios
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app'
  : 'http://localhost:3001';

axios.defaults.baseURL = API_BASE_URL;
axios.defaults.timeout = 30000; // 30 segundos timeout para evitar timeouts em produção
axios.defaults.withCredentials = true;
// Header custom exigido pela proteção CSRF do backend em toda escrita. Um <form>
// malicioso cross-site não consegue setá-lo; a SPA seta em todas as requisições.
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.interceptors.request.use((config) => {
  if (config?.headers) {
    delete config.headers.Authorization;
    delete config.headers.authorization;
  }
  return config;
});

// Context de Autenticação
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  
  // Logs de debug controlados (apenas quando necessário)
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_AUTH === 'true') {
    console.log('🔐 useAuth - Context:', {
      isAuthenticated: !!context.user,
      loading: context.loading,
      user: context.user?.username,
      hasUser: !!context.user
    });
  }
  
  return context;
};

// Provider de Autenticação
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const lastActivityRef = useRef(Date.now());

  const logout = useCallback((options = {}) => {
    const {
      remote = true,
      notify = true
    } = options;

    if (remote) {
      void axios.post('/api/auth/logout').catch(() => {});
    }

    setUser(null);
    if (notify) {
      toast.info('Sessão finalizada com sucesso');
    }
  }, []);

  // Configurações de inatividade
  const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutos em milissegundos

  // Verificar inatividade (30 minutos sem atividade)
  const checkInactivity = useCallback(() => {
    const timeSinceLastActivity = Date.now() - lastActivityRef.current;
    if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('🕐 Sessão expirada por inatividade');
      }
      toast.warning('Sessão expirada por inatividade');
      logout();
      return true;
    }
    return false;
  }, [logout, INACTIVITY_TIMEOUT]);

  // Atualizar atividade do usuário
  const updateActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    // ponytail: ref drives the inactivity check on every event; state only feeds the
    // "X min atrás" display, so push it at most once/min. Pushing on every mousemove
    // changed the context value at 60fps and re-rendered the whole app.
    setLastActivity((prev) => (now - prev > 60000 ? now : prev));
  }, []);

  const checkAuthStatus = useCallback(async () => {
    try {
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_AUTH === 'true') {
        console.log('🔍 Verificando status de autenticação...');
      }

      // Verificar inatividade
      if (checkInactivity()) {
        return;
      }

      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_AUTH === 'true') {
        console.log('✅ Token válido, verificando com servidor...');
      }
      const response = await axios.get('/api/auth/me');
      if (process.env.NODE_ENV !== 'production') {
        console.log('✅ Resposta do servidor:', response.data);
      }
      setUser(response.data.user);
      updateActivity();
    } catch (error) {
      if (error.response?.status === 401) {
        setUser(null);
        return;
      }

      if (error.code === 'ECONNABORTED' || !error.response) {
        console.error('❌ Timeout/erro de rede na verificação de auth:', error);
        return;
      }

      console.error('❌ Erro na verificação de auth:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [logout, checkInactivity, updateActivity]);

  // Listener para atividade do usuário
  useEffect(() => {
    if (user) {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      
      const activityHandler = () => {
        updateActivity();
      };

      events.forEach(event => {
        document.addEventListener(event, activityHandler, true);
      });

      return () => {
        events.forEach(event => {
          document.removeEventListener(event, activityHandler, true);
        });
      };
    }
  }, [user, updateActivity]);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = async (username, password) => {
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_AUTH === 'true') {
      console.log('🔐 Iniciando processo de login...');
      console.log('📝 Username:', username);
    }
    
    try {
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_AUTH === 'true') {
        console.log('🌐 Fazendo requisição para /api/auth/login...');
      }
      const response = await axios.post('/api/auth/login', {
        username,
        password
      });

      if (process.env.NODE_ENV !== 'production') {
        console.log('✅ Resposta do servidor recebida:', response.data);
      }

      // Segundo fator ligado: ainda não há sessão. Devolvemos o desafio para a
      // tela pedir o código, sem tocar em setUser.
      if (response.data?.requires2fa) {
        return { requires2fa: true, challengeId: response.data.challengeId, destino: response.data.destino };
      }

      const { user: userData } = response.data;

      setUser(userData);
      updateActivity();

      if (process.env.NODE_ENV !== 'production') {
        console.log('✅ Login bem-sucedido!');
      }
      toast.success('Login realizado com sucesso!');

      return userData;
    } catch (error) {
      console.error('❌ Erro no login:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao fazer login';
      toast.error(errorMessage);
      return false;
    }
  };

  const verify2fa = async (challengeId, code) => {
    try {
      const response = await axios.post('/api/auth/verify-2fa', { challengeId, code });
      const { user: userData } = response.data;

      setUser(userData);
      updateActivity();
      toast.success('Login realizado com sucesso!');

      return userData;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Não foi possível validar o código';
      toast.error(errorMessage);
      return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    verify2fa,
    logout,
    isAuthenticated: !!user,
    lastActivity
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Componente de Login
const LoginPage = () => {
  const { login, verify2fa, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  // Desafio de segundo fator pendente: enquanto existir, a tela pede o código.
  const [desafio2fa, setDesafio2fa] = useState(null);
  const [codigo2fa, setCodigo2fa] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      // Redirecionar baseado no role do usuário
      if (user?.role === 'super_admin' || user?.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (user?.role === 'client_admin') {
        navigate('/admin/dashboard');
      } else if (user?.role === 'investigador_externo') {
        navigate('/investigation');
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, navigate, user]);

  const redirecionarPorRole = (result) => {
    if (result?.role === 'super_admin' || result?.role === 'admin') {
      navigate('/admin/dashboard');
    } else if (result?.role === 'client_admin') {
      navigate('/admin/dashboard');
    } else if (result?.role === 'investigador_externo') {
      navigate('/investigation');
    } else {
      navigate('/');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.username, formData.password);

    // Senha certa, mas falta o segundo fator: troca a tela pela do código.
    if (result && result.requires2fa) {
      setDesafio2fa(result);
      setCodigo2fa('');
      setLoading(false);
      return;
    }

    if (result) redirecionarPorRole(result);
    setLoading(false);
  };

  const handleVerificar2fa = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await verify2fa(desafio2fa.challengeId, codigo2fa.trim());
    if (result) redirecionarPorRole(result);
    setLoading(false);
  };

  const voltarParaSenha = () => {
    setDesafio2fa(null);
    setCodigo2fa('');
    setFormData({ username: formData.username, password: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <BrandMark tone="dark" height={64} className="mx-auto" decorative />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            REPORTFius
          </h2>
          <p className="mt-2 text-center text-sm text-blue-200">
            Canal de Denúncias Corporativas
          </p>
        </div>
        {desafio2fa ? (
          <form className="mt-8 space-y-6" onSubmit={handleVerificar2fa}>
            <div className="rounded-md bg-slate-800/60 p-4 text-sm text-blue-100">
              Enviamos um código de 6 dígitos para <strong>{desafio2fa.destino}</strong>.
              Ele vale por poucos minutos e só pode ser usado uma vez.
            </div>
            <div>
              <label htmlFor="codigo2fa" className="sr-only">Código de acesso</label>
              <input
                id="codigo2fa"
                name="codigo2fa"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                autoFocus
                className="appearance-none relative block w-full px-3 py-3 border border-slate-300 placeholder-slate-500 text-slate-900 rounded-md text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-2xl"
                placeholder="000000"
                value={codigo2fa}
                onChange={(e) => setCodigo2fa(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <button
              type="submit"
              disabled={loading || codigo2fa.length < 6}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Verificando...
                </div>
              ) : (
                'Confirmar código'
              )}
            </button>
            <button
              type="button"
              onClick={voltarParaSenha}
              className="w-full text-sm font-medium text-blue-200 hover:text-blue-100"
            >
              Voltar
            </button>
          </form>
        ) : (
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="username" className="sr-only">Nome de usuário</label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-slate-300 placeholder-slate-500 text-slate-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Nome de usuário"
                value={formData.username}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Senha</label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-slate-300 placeholder-slate-500 text-slate-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Senha"
                value={formData.password}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Entrando...
                </div>
              ) : (
                'Entrar'
              )}
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <a href="/forgot-password" className="font-medium text-blue-200 hover:text-blue-100">
                Esqueceu sua senha?
              </a>
            </div>
            <div className="text-sm">
              <a href="/register" className="font-medium text-blue-200 hover:text-blue-100">
                Criar conta
              </a>
            </div>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

// Componente de Informações da Sessão
const SessionInfo = () => {
  const { user, lastActivity } = useAuth();
  const location = useLocation();
  
  if (!user) return null;

  // Esconder em páginas com sidebar (MainLayout)
  const pagesWithSidebar = [
    '/admin/dashboard',
    '/investigation', 
    '/users',
    '/audit',
    '/forms',
    '/executive/dashboard'
  ];
  
  if (pagesWithSidebar.some(page => location.pathname.startsWith(page))) {
    return null;
  }

  const formatLastActivity = () => {
    if (!lastActivity) return 'N/A';
    const now = Date.now();
    const diff = now - lastActivity;
    const minutes = Math.floor(diff / 60000);
    return `${minutes} min atrás`;
  };

  return (
    <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border border-slate-200 p-3 text-xs text-slate-600 max-w-xs z-10">
      <div className="font-semibold mb-1">Sessão Ativa</div>
      <div>Usuário: {user.username}</div>
      <div>Última atividade: {formatLastActivity()}</div>
    </div>
  );
};


// Componente de Proteção de Rotas
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl shadow-2xl mb-6">
            <svg className="w-10 h-10 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium">Carregando REPORTFius...</p>
          <p className="text-blue-200 text-sm mt-2">Verificando credenciais...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Componente de Proteção para Admin Global
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  // Logs movidos para useEffect para evitar loops
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔒 AdminRoute - Debug:', { isAuthenticated, loading, user });
      console.log('🔒 AdminRoute - User details:', {
        id: user?.id,
        username: user?.username,
        role: user?.role,
        is_admin: user?.is_admin,
        is_client_admin: user?.is_client_admin,
        client_slug: user?.client_slug
      });
    }
  }, [isAuthenticated, loading, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl shadow-2xl mb-6">
            <svg className="w-10 h-10 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2zm0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium">Carregando REPORTFius...</p>
          <p className="text-blue-200 text-sm mt-2">Verificando credenciais...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('🔒 AdminRoute - Usuário não autenticado, redirecionando para login');
    return <Navigate to="/login" replace />;
  }

  // Permitir admin global, super_admin, admin-cliente OU investigador externo
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin' || user?.is_admin;
  const isClientAdmin = user?.role === 'client_admin' || user?.is_client_admin;
  const isInvestigador = user?.role === 'investigador_externo' || user?.is_investigador_externo;
  
  if (!isAdmin && !isClientAdmin && !isInvestigador) {
    console.log('🔒 AdminRoute - Usuário não tem permissão, redirecionando para home');
    console.log('🔒 AdminRoute - User data:', user);
    console.log('🔒 AdminRoute - Verificação de permissão:', {
      role: user?.role,
      isAdmin,
      isClientAdmin,
      isInvestigador,
      is_admin: user?.is_admin,
      is_client_admin: user?.is_client_admin,
      is_investigador_externo: user?.is_investigador_externo
    });
    return <Navigate to="/" replace />;
  }

  // Log movido para useEffect acima para evitar loops
  return children;
};

// Componente de Proteção para Admin Global Apenas
const GlobalAdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl shadow-2xl mb-6">
            <svg className="w-10 h-10 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium">Carregando REPORTFius...</p>
          <p className="text-blue-200 text-sm mt-2">Verificando credenciais...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Permitir apenas admin global
  const isGlobalAdmin = user?.role === 'super_admin' || user?.role === 'admin' || user?.is_admin === true;
  if (!isGlobalAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Componente de Proteção para Admin Geral ou Investigador
const ApprovalRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl shadow-2xl mb-6">
            <svg className="w-10 h-10 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium">Carregando REPORTFius...</p>
          <p className="text-blue-200 text-sm mt-2">Verificando credenciais...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Permitir apenas admin geral ou investigador externo
  const isAdmin =
    user?.role === 'super_admin' ||
    user?.role === 'admin' ||
    user?.is_admin === true;
  const isInvestigator = user?.is_investigador_externo === true;
  
  if (!isAdmin && !isInvestigator) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Componente wrapper para extrair o slug da URL
const PublicComplaintFormWrapper = () => {
  const { slug } = useParams();
  return USE_NEW_FORM ? <PublicComplaintFormV2 clientSlug={slug} /> : <PublicComplaintForm clientSlug={slug} />;
};

// App Principal
const App = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Landing Page como página inicial (nova identidade visual) */}
            <Route path="/" element={<LandingPageV2 />} />
            {/* Landing anterior mantida para comparação/rollback */}
            <Route path="/landing-antiga" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />

            {/* Rotas públicas */}
            <Route path="/track/:token" element={<ComplaintTracker />} />

            {/* Rotas de autenticação */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/register" element={<UserRegister />} />

            {/* Página de Segurança */}
            <Route path="/seguranca" element={<SecurityPage />} />

            {/* Rotas protegidas - Usuários comuns */}
            
            {/* Rotas protegidas - Apenas Admin Global */}
            <Route path="/users" element={
              <GlobalAdminRoute>
                <MainLayout>
                  <SimpleUserManagement />
                </MainLayout>
              </GlobalAdminRoute>
            } />
            <Route path="/audit" element={
              <GlobalAdminRoute>
                <MainLayout>
                  <AuditLogsPage />
                </MainLayout>
              </GlobalAdminRoute>
            } />
            <Route path="/admin/policies" element={
              <GlobalAdminRoute>
                <MainLayout>
                  <PoliciesManager />
                </MainLayout>
              </GlobalAdminRoute>
            } />
            <Route path="/approval" element={
              <ApprovalRoute>
                <MainLayout>
                  <ComplaintApproval />
                </MainLayout>
              </ApprovalRoute>
            } />
            <Route path="/forms" element={
              <AdminRoute>
                <MainLayout>
                  <SimpleFormManagement />
                </MainLayout>
              </AdminRoute>
            } />
            
            {/* Rotas admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={
              <AdminRoute>
                <MainLayout>
                  <AdminDashboard />
                </MainLayout>
              </AdminRoute>
            } />
            <Route path="/admin/dashboard-old" element={
              <AdminRoute>
                <MainLayout>
                  <AdminDashboard />
                </MainLayout>
              </AdminRoute>
            } />
            <Route path="/client-admin" element={
              <AdminRoute>
                <MainLayout>
                  <ModernDashboard />
                </MainLayout>
              </AdminRoute>
            } />
            <Route path="/admin/modern" element={
              <AdminRoute>
                <MainLayout>
                  <ModernDashboard />
                </MainLayout>
              </AdminRoute>
            } />
            <Route path="/executive" element={
              <AdminRoute>
                <MainLayout>
                  <ExecutiveDashboard />
                </MainLayout>
              </AdminRoute>
            } />
            <Route path="/investigation" element={
              <AdminRoute>
                <MainLayout>
                  <InvestigationDashboard />
                </MainLayout>
              </AdminRoute>
            } />
            <Route path="/forms/builder" element={
              <AdminRoute>
                <MainLayout>
                  <FormBuilder />
                </MainLayout>
              </AdminRoute>
            } />
            
            {/* Rota protegida para dashboard interativo (analytics avançado) */}
            <Route path="/analytics-disruptive" element={
              <AdminRoute>
                <MainLayout>
                  <AnalyticsSwitch />
                </MainLayout>
              </AdminRoute>
            } />
            
            {/* Dashboard Super Simples com Modais */}
            <Route path="/simple" element={
              <AdminRoute>
                <MainLayout>
                  <SuperSimpleDashboard />
                </MainLayout>
              </AdminRoute>
            } />
            
            
            {/* Rota dinâmica para formulários públicos - deve ficar por último */}
            <Route path="/:slug" element={<PublicComplaintFormWrapper />} />
            
            {/* Rota catch-all: qualquer rota não encontrada vai para login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          
          <SessionInfo />
          
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App; 
